import React, { useMemo, useState } from 'react';
import DashboardBarChart from './charts/DashboardBarChart';
import { EMAIL_STATS_WINDOWS, SMS_STATS_CATEGORIES } from '../dashboardStatsConfig';
import { parseStatValue } from '../dashboardStatUtils';
import { VIBRANT_PALETTE } from '../dashboardChartColors';

const SENT_COLOR = '#0dcaf0';
const ALL_REMINDER_VALUE = 'ALL';

function smsStatKey(keyPrefix, days) {
    return `${keyPrefix}_${days}_DAYS`;
}

function sumSmsSent(getStat, categories, days) {
    return categories.reduce(
        (total, category) => total + parseStatValue(getStat(smsStatKey(category.keyPrefix, days))),
        0
    );
}

function buildSmsCategoryChartSeries(getStat, categories, days) {
    return categories
        .map((category, index) => {
            const sent = parseStatValue(getStat(smsStatKey(category.keyPrefix, days)));

            return {
                key: category.keyPrefix,
                label: category.label,
                color: VIBRANT_PALETTE[index % VIBRANT_PALETTE.length],
                value: sent,
                tooltipExtra: `Sent: ${sent.toLocaleString()}`,
            };
        })
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value);
}

function DashboardSmsStats({ getStat, isLoading, loadError, lastUpdated }) {
    const [days, setDays] = useState(30);
    const [selectedReminder, setSelectedReminder] = useState(ALL_REMINDER_VALUE);

    const isAllSelected = selectedReminder === ALL_REMINDER_VALUE;

    const selectedCategory = useMemo(() => {
        if (isAllSelected) {
            return { keyPrefix: ALL_REMINDER_VALUE, label: 'All SMS' };
        }

        return (
            SMS_STATS_CATEGORIES.find((category) => category.keyPrefix === selectedReminder) ||
            SMS_STATS_CATEGORIES[0]
        );
    }, [isAllSelected, selectedReminder]);

    const sent = isAllSelected
        ? sumSmsSent(getStat, SMS_STATS_CATEGORIES, days)
        : parseStatValue(getStat(smsStatKey(selectedCategory.keyPrefix, days)));
    const hasData = sent > 0;

    const chartSeries = useMemo(() => {
        if (isAllSelected) {
            return buildSmsCategoryChartSeries(getStat, SMS_STATS_CATEGORIES, days);
        }

        return [{ key: 'sent', label: 'Sent', color: SENT_COLOR, value: sent }];
    }, [days, getStat, isAllSelected, sent]);

    const emptyLabel = isAllSelected ? 'SMS' : selectedCategory.label.toLowerCase();

    const subtitle = lastUpdated
        ? isAllSelected
            ? `Sent by reminder · Updated ${lastUpdated}`
            : `Sent for ${selectedCategory.label} · Updated ${lastUpdated}`
        : 'Select a reminder to view sent SMS';

    return (
        <section className="ecx-page-card ecx-page-panel dashboard-stats-panel dashboard-panel-theme dashboard-panel-theme--cyan">
            <div className="ecx-page-panel__header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="dashboard-stats-panel__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-chat-dots" />
                        </span>
                        <div>
                            <h2 className="dashboard-stats-panel__title">SMS Stats</h2>
                            <p className="dashboard-stats-panel__subtitle mb-0">{subtitle}</p>
                        </div>
                    </div>
                    <div className="dashboard-email-stats__controls">
                        <label className="dashboard-email-stats__select-wrap">
                            <span className="dashboard-email-stats__select-label">Reminder</span>
                            <select
                                className="form-select form-select-sm dashboard-email-stats__select"
                                value={selectedReminder}
                                onChange={(event) => setSelectedReminder(event.target.value)}
                                aria-label="SMS reminder type"
                            >
                                <option value={ALL_REMINDER_VALUE}>All</option>
                                {SMS_STATS_CATEGORIES.map((category) => (
                                    <option key={category.keyPrefix} value={category.keyPrefix}>
                                        {category.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <div className="dashboard-email-stats__toggle" role="group" aria-label="SMS stats period">
                            {EMAIL_STATS_WINDOWS.map((window) => (
                                <button
                                    key={window.days}
                                    type="button"
                                    className={`dashboard-email-stats__toggle-btn${days === window.days ? ' is-active' : ''}`}
                                    onClick={() => setDays(window.days)}
                                >
                                    {window.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="ecx-page-panel__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="dashboard-stat-tile dashboard-stat-tile--skeleton" style={{ minHeight: 280 }} />
                ) : (
                    <>
                        <div className="dashboard-email-stats__summary">
                            <div className="dashboard-email-stats__kpi">
                                <span className="dashboard-email-stats__kpi-label">Sent</span>
                                <strong className="dashboard-email-stats__kpi-value">{sent.toLocaleString()}</strong>
                            </div>
                        </div>

                        {!hasData ? (
                            <div className="dashboard-empty-state">
                                <i className="bi bi-bar-chart me-2" />
                                No {emptyLabel} in the last {days} days
                            </div>
                        ) : (
                            <div className="dashboard-email-stats__chart">
                                <DashboardBarChart
                                    series={chartSeries}
                                    solidColors
                                    showLegend
                                    horizontal={isAllSelected}
                                    seriesName="Sent"
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}

export default DashboardSmsStats;
